/**
 * Keyboard hint helpers derived from core KEY_MAP (never hardcode bindings in the UI).
 * Two clusters: the WASD hand (Space/Shift/F/Q/E + J/K/L/I for the other hand) and the arrows
 * hand (Z/X/C/V). Extras (/, RCtrl, RShift, ., Enter) appear on the Controls screen.
 */
import { KEY_MAP, type Action } from '@core/input';

/** Keys used alongside WASD (left-hand Space/Shift/F/Q/E, right-hand J/K/L/I). */
export const WASD_SIDE = ['Space', 'ShiftLeft', 'KeyF', 'KeyQ', 'KeyE', 'KeyJ', 'KeyK', 'KeyL', 'KeyI'];
/** Keys used alongside the arrow keys. */
export const ARROW_SIDE = ['KeyZ', 'KeyX', 'KeyC', 'KeyV', 'Slash', 'ControlRight', 'ShiftRight', 'Period', 'Enter'];

const LABELS: Record<string, string> = {
  Space: 'Space',
  ShiftLeft: 'Shift',
  ShiftRight: 'R-Shift',
  ControlRight: 'R-Ctrl',
  ControlLeft: 'Ctrl',
  CapsLock: 'Caps',
  Comma: ',',
  Slash: '/',
  Period: '.',
  Enter: 'Enter',
  Escape: 'Esc',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

export function keyLabel(code: string): string {
  return LABELS[code] ?? code.replace(/^Key|^Digit/, '');
}

/** Codes bound to `action`, split per cluster, in KEY_MAP order within each cluster list. */
export function keysFor(action: Action): { wasd: string[]; arrows: string[] } {
  const bound = (list: string[]) => list.filter((c) => KEY_MAP[c] === action);
  return { wasd: bound(WASD_SIDE), arrows: bound(ARROW_SIDE) };
}

/** Compact hint for HUD chips: primary WASD-side keys + the first arrows-side key. */
export function compactKeys(action: Action): { wasd: string[]; arrows: string[] } {
  const k = keysFor(action);
  return { wasd: k.wasd.slice(0, 2), arrows: k.arrows.slice(0, 1) };
}

/** Plain-text hint, e.g. "F/J · Z". */
export function hintText(action: Action): string {
  const k = compactKeys(action);
  const a = k.wasd.map(keyLabel).join('/');
  const b = k.arrows.map(keyLabel).join('/');
  return a && b ? `${a} · ${b}` : a || b;
}

/** HTML hint: <kbd> chips with a subtle separator between clusters. */
export function hintHtml(action: Action): string {
  const k = compactKeys(action);
  const chips = (codes: string[]) => codes.map((c) => `<kbd>${keyLabel(c)}</kbd>`).join('');
  const a = chips(k.wasd);
  const b = chips(k.arrows);
  return a && b ? `${a}<i class="kor">·</i>${b}` : a || b;
}
